import {
  rsosApi,
  baseQueryArgs,
  baseExtraOptions,
  CURRENT_ORGANIZATION_NAME,
} from '../rsosApi';
import {
  parseDigitalDispatchListRows,
  parseTowProRequests,
  TowProMunicipalitiesResponseSchema,
  type DigitalDispatchListRow,
  TowProRequestResponseSchema,
  TowProVinLookupResponseSchema,
  type DigitalDispatchQueueState,
  type TowProCancelRequest,
  type TowProDispatchRequest,
  type TowProMunicipalitiesResponse,
  type TowProRequestResponse,
  type TowProVinLookupResponse,
} from './digitalDispatchSchemas';
import { markExpectedError } from './etlIntegrationsApi';
import {
  parseNorfolkSouthernRequests,
  NorfolkSouthernRequestResponseSchema,
  type NorfolkSouthernConsistRequest,
  type NorfolkSouthernRequestResponse,
} from './norfolkSouthernSchemas';

/**
 * We extend @see rsosApi as to let it deal with refresh tokens and retries for
 * us. Digital dispatch data gets cached under the api key in emergency data's
 * root reducer.
 */
const enhancedApi = rsosApi.enhanceEndpoints({
  addTagTypes: ['DigitalDispatchRequests'],
});

/**
 * RTK Query API for the etl_integrations digital dispatch framework. The
 * list and queue-state endpoints are shared across integrations; the rest
 * are specific to each integration.
 */
const digitalDispatchApi = enhancedApi.injectEndpoints({
  endpoints: builder => ({
    // Every integration's requests for the account, unparsed. Fetched once
    // and shared; each integration reads its own rows through a selector
    // (see useGetTowProRequestsQuery and useGetNorfolkSouthernRequestsQuery).
    getDigitalDispatchRequests: builder.query<DigitalDispatchListRow[], void>({
      query: () => ({
        ...baseQueryArgs,
        url: 'v1/etl-integrations/api/digital-dispatch/requests',
        method: 'GET',
        headers: {
          ...baseQueryArgs.headers,
          // CURRENT_ORGANIZATION_NAME causes rsosApi to fetch use the current org name for us
          'X-RapidSOS-Org': CURRENT_ORGANIZATION_NAME,
        },
      }),
      transformResponse: (response: unknown): DigitalDispatchListRow[] =>
        parseDigitalDispatchListRows(response),
      transformErrorResponse: markExpectedError,
      providesTags: ['DigitalDispatchRequests'],
      extraOptions: { ...baseExtraOptions },
    }),
    createTowProRequest: builder.mutation<
      TowProRequestResponse,
      TowProDispatchRequest
    >({
      query: body => ({
        ...baseQueryArgs,
        url: 'v1/etl-integrations/api/towpro/requests',
        method: 'POST',
        body,
        headers: {
          ...baseQueryArgs.headers,
          'Content-Type': 'application/json',
          'X-RapidSOS-Org': CURRENT_ORGANIZATION_NAME,
        },
      }),
      transformResponse: (response: unknown): TowProRequestResponse =>
        TowProRequestResponseSchema.parse(response),
      transformErrorResponse: markExpectedError,
      invalidatesTags: ['DigitalDispatchRequests'],
      extraOptions: { ...baseExtraOptions },
    }),
    updateTowProRequest: builder.mutation<
      TowProRequestResponse,
      { requestId: number; body: TowProDispatchRequest }
    >({
      query: ({ requestId, body }) => ({
        ...baseQueryArgs,
        url: `v1/etl-integrations/api/towpro/requests/${requestId}`,
        method: 'PUT',
        body,
        headers: {
          ...baseQueryArgs.headers,
          'Content-Type': 'application/json',
          'X-RapidSOS-Org': CURRENT_ORGANIZATION_NAME,
        },
      }),
      transformResponse: (response: unknown): TowProRequestResponse =>
        TowProRequestResponseSchema.parse(response),
      transformErrorResponse: markExpectedError,
      invalidatesTags: ['DigitalDispatchRequests'],
      extraOptions: { ...baseExtraOptions },
    }),
    cancelTowProRequest: builder.mutation<
      TowProRequestResponse,
      { requestId: number; body: TowProCancelRequest }
    >({
      query: ({ requestId, body }) => ({
        ...baseQueryArgs,
        url: `v1/etl-integrations/api/towpro/requests/${requestId}/cancel`,
        method: 'POST',
        body,
        headers: {
          ...baseQueryArgs.headers,
          'Content-Type': 'application/json',
          'X-RapidSOS-Org': CURRENT_ORGANIZATION_NAME,
        },
      }),
      transformResponse: (response: unknown): TowProRequestResponse =>
        TowProRequestResponseSchema.parse(response),
      transformErrorResponse: markExpectedError,
      invalidatesTags: ['DigitalDispatchRequests'],
      extraOptions: { ...baseExtraOptions },
    }),
    setTowProQueueState: builder.mutation<
      TowProRequestResponse,
      { requestId: number; state: DigitalDispatchQueueState }
    >({
      query: ({ requestId, state }) => ({
        ...baseQueryArgs,
        url: `v1/etl-integrations/api/digital-dispatch/requests/${requestId}/queue-state`,
        method: 'PUT',
        body: { state },
        headers: {
          ...baseQueryArgs.headers,
          'Content-Type': 'application/json',
          'X-RapidSOS-Org': CURRENT_ORGANIZATION_NAME,
        },
      }),
      transformResponse: (response: unknown): TowProRequestResponse =>
        TowProRequestResponseSchema.parse(response),
      async onQueryStarted({ requestId, state }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          digitalDispatchApi.util.updateQueryData(
            'getDigitalDispatchRequests',
            undefined,
            draft => {
              const request = draft.find(r => r.id === requestId);
              if (request) {
                request.queueState = state;
              }
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch (error) {
          patch.undo();
          // eslint-disable-next-line no-console -- track errors in sentry
          console.error('Failed to update request queue state', error);
        }
      },
      transformErrorResponse: markExpectedError,
      invalidatesTags: ['DigitalDispatchRequests'],
      extraOptions: { ...baseExtraOptions },
    }),
    createNorfolkSouthernRequest: builder.mutation<
      NorfolkSouthernRequestResponse,
      NorfolkSouthernConsistRequest
    >({
      query: body => ({
        ...baseQueryArgs,
        url: 'v1/etl-integrations/api/norfolk-southern/requests',
        method: 'POST',
        body,
        headers: {
          ...baseQueryArgs.headers,
          'Content-Type': 'application/json',
          'X-RapidSOS-Org': CURRENT_ORGANIZATION_NAME,
        },
      }),
      transformResponse: (response: unknown): NorfolkSouthernRequestResponse =>
        NorfolkSouthernRequestResponseSchema.parse(response),
      transformErrorResponse: markExpectedError,
      invalidatesTags: ['DigitalDispatchRequests'],
      extraOptions: { ...baseExtraOptions },
    }),
    setNorfolkSouthernQueueState: builder.mutation<
      NorfolkSouthernRequestResponse,
      { requestId: number; state: DigitalDispatchQueueState }
    >({
      query: ({ requestId, state }) => ({
        ...baseQueryArgs,
        url: `v1/etl-integrations/api/digital-dispatch/requests/${requestId}/queue-state`,
        method: 'PUT',
        body: { state },
        headers: {
          ...baseQueryArgs.headers,
          'Content-Type': 'application/json',
          'X-RapidSOS-Org': CURRENT_ORGANIZATION_NAME,
        },
      }),
      transformResponse: (response: unknown): NorfolkSouthernRequestResponse =>
        NorfolkSouthernRequestResponseSchema.parse(response),
      async onQueryStarted({ requestId, state }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          digitalDispatchApi.util.updateQueryData(
            'getDigitalDispatchRequests',
            undefined,
            draft => {
              const request = draft.find(r => r.id === requestId);
              if (request) {
                request.queueState = state;
              }
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch (error) {
          patch.undo();
          // eslint-disable-next-line no-console -- track errors in sentry
          console.error('Failed to update request queue state', error);
        }
      },
      transformErrorResponse: markExpectedError,
      invalidatesTags: ['DigitalDispatchRequests'],
      extraOptions: { ...baseExtraOptions },
    }),
    getTowProMunicipalities: builder.query<TowProMunicipalitiesResponse, void>({
      query: () => ({
        ...baseQueryArgs,
        url: 'v1/etl-integrations/api/towpro/municipalities',
        method: 'GET',
        headers: {
          ...baseQueryArgs.headers,
          'X-RapidSOS-Org': CURRENT_ORGANIZATION_NAME,
        },
      }),
      transformResponse: (response: unknown): TowProMunicipalitiesResponse =>
        TowProMunicipalitiesResponseSchema.parse(response),
      transformErrorResponse: markExpectedError,
      extraOptions: { ...baseExtraOptions },
    }),
    getTowProVinLookup: builder.query<TowProVinLookupResponse, string>({
      query: vin => ({
        ...baseQueryArgs,
        url: `v1/etl-integrations/api/towpro/vin-lookup/${encodeURIComponent(vin)}`,
        method: 'GET',
        headers: {
          ...baseQueryArgs.headers,
          'X-RapidSOS-Org': CURRENT_ORGANIZATION_NAME,
        },
      }),
      transformResponse: (response: unknown): TowProVinLookupResponse =>
        TowProVinLookupResponseSchema.parse(response),
      transformErrorResponse: markExpectedError,
      extraOptions: { ...baseExtraOptions },
    }),
  }),
  overrideExisting: false,
});

const {
  useGetDigitalDispatchRequestsQuery,
  useCreateTowProRequestMutation,
  useUpdateTowProRequestMutation,
  useCancelTowProRequestMutation,
  useSetTowProQueueStateMutation,
  useGetTowProMunicipalitiesQuery,
  useGetTowProVinLookupQuery,
  useLazyGetTowProVinLookupQuery,
  useCreateNorfolkSouthernRequestMutation,
  useSetNorfolkSouthernQueueStateMutation,
} = digitalDispatchApi;

type ListQueryOptions = NonNullable<
  Parameters<typeof useGetDigitalDispatchRequestsQuery>[1]
>;

/**
 * Parses the shared list for one integration, remembering the result per
 * raw list so a component reading it re-renders only when the list changes.
 */
function memoizedListParser<Row>(
  parse: (rows: unknown) => Row[],
): (rows: DigitalDispatchListRow[] | undefined) => Row[] | undefined {
  const parsed = new WeakMap<DigitalDispatchListRow[], Row[]>();
  return rows => {
    if (!rows) {
      return undefined;
    }
    let result = parsed.get(rows);
    if (!result) {
      result = parse(rows);
      parsed.set(rows, result);
    }
    return result;
  };
}

const selectTowProRequests = memoizedListParser(parseTowProRequests);
const selectNorfolkSouthernRequests = memoizedListParser(
  parseNorfolkSouthernRequests,
);

/** TowPro's rows from the shared digital dispatch list. */
function useGetTowProRequestsQuery(
  _arg?: void,
  options?: Omit<ListQueryOptions, 'selectFromResult'>,
) {
  return useGetDigitalDispatchRequestsQuery(undefined, {
    ...options,
    selectFromResult: result => ({
      ...result,
      data: selectTowProRequests(result.data),
    }),
  });
}

/** Norfolk Southern's rows from the shared digital dispatch list. */
function useGetNorfolkSouthernRequestsQuery(
  _arg?: void,
  options?: Omit<ListQueryOptions, 'selectFromResult'>,
) {
  return useGetDigitalDispatchRequestsQuery(undefined, {
    ...options,
    selectFromResult: result => ({
      ...result,
      data: selectNorfolkSouthernRequests(result.data),
    }),
  });
}

export const DIGITAL_DISPATCH_EVENT_TYPES = {
  RESOURCE_REQUESTED: 'digital-dispatch-resource-requested',
  RESOURCE_UPDATED: 'digital-dispatch-resource-updated',
  RESOURCE_CANCELED: 'digital-dispatch-resource-canceled',
  RESOURCE_ACTIVATED: 'digital-dispatch-resource-activated',
  STATUS_UPDATED: 'digital-dispatch-status-updated',
} as const;

export {
  digitalDispatchApi,
  useGetDigitalDispatchRequestsQuery,
  useGetTowProRequestsQuery,
  useCreateTowProRequestMutation,
  useUpdateTowProRequestMutation,
  useCancelTowProRequestMutation,
  useSetTowProQueueStateMutation,
  useGetTowProMunicipalitiesQuery,
  useGetTowProVinLookupQuery,
  useLazyGetTowProVinLookupQuery,
  useGetNorfolkSouthernRequestsQuery,
  useCreateNorfolkSouthernRequestMutation,
  useSetNorfolkSouthernQueueStateMutation,
};
